import React, { useCallback, useEffect, useRef, useState } from "react";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { MAIN_URL } from "../../../Configurations/Urls";
import {
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const CONTACT_TOOLBAR_FILTERS = [
  {
    urlKey: "recruitment_source",
    field: "organization_recruitment_source_id",
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_recruitment_source_contact_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => {
            const trimmed = v.trim();
            return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
          });
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "contact_person_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const normalizeAppliedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((f) => f?.field && f?.operator)
    .map((f) => {
      const requiresValue = operatorRequiresValue(f.operator);
      if (!requiresValue) {
        return { field: f.field, operator: f.operator, value: null };
      }

      let value = f.value;
      if (value === null || value === undefined || value === "") return null;
      if (Array.isArray(value) && value.length === 0) return null;

      if (["equals", "not_equals"].includes(f.operator) && typeof value === "string") {
        value = value.split(",").map((v) => {
          const trimmed = v.trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      } else if (
        ["equals", "not_equals"].includes(f.operator) &&
        Array.isArray(value)
      ) {
        value = value.map((v) => {
          const trimmed = String(v).trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      }

      return { field: f.field, operator: f.operator, value };
    })
    .filter(Boolean);
};

const DEFAULT_COLUMNS = [
  {
    field: "contact_person_name",
    label: "Contact Person",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "recruitment_source_name",
    label: "Recruitment Source",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "designation",
    label: "Designation",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "phone_number",
    label: "Phone",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "email",
    label: "Email",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_primary",
    label: "Primary",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_active",
    label: "Is Active",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function RecruitmentSourceContactsList() {
  const [rows, setRows] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [recruitmentSources, setRecruitmentSources] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }
      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (configRes.ok) {
          const configResponse = await configRes.json();
          const orgKey = `organization_recruitment_source_contacts_grid_${org.organization_id}`;
          const savedConfig = (configResponse.datagrids || []).find(
            (dg) => dg.datagrid_key === orgKey
          );
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            setConfigColumns(serverCfg?.columns?.length ? serverCfg.columns : DEFAULT_COLUMNS);
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error(error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) setSearchParams(params, { replace: true });
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetch(
      `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source?active_only=1`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        if (cancelled) return;
        setRecruitmentSources(asOptionList(data, ["recruitment_sources"]));
      })
      .catch((error) => {
        console.error("Failed to load recruitment sources", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const requestBody = {
          filters: mergeToolbarFilters(
            appliedFilters !== null
              ? normalizeAppliedFilters(appliedFilters)
              : buildFiltersFromParams(searchParams),
            searchParams,
            CONTACT_TOOLBAR_FILTERS
          ),
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field:
              searchParams.get("sortBy") ||
              "organization_recruitment_source_contact_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-contacts-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page: result.pagination.page || 1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setRows(
          data.map((item) => ({
            ...item,
            id: item?.organization_recruitment_source_contact_id,
            is_primary:
              item?.is_primary == 1 || item?.is_primary === true ? "✔" : "✖",
            is_active: item?.is_active == 1 || item?.is_active === true ? "✔" : "✖",
          }))
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error(error);
        toast.error("Failed to load contacts");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [org?.organization_id, searchParams, appliedFilters]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-contact/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Contact deleted");
          setAppliedFilters((prev) => (prev === null ? [] : [...prev]));
        } else {
          const errorData = await response.json();
          toast.error(errorData.message || "Failed to delete contact");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete contact");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/recruitment/source-contacts/edit/${item.id || item.organization_recruitment_source_contact_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/recruitment/source-contacts/view/${item.id || item.organization_recruitment_source_contact_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback((nextFilters) => {
    setAppliedFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Recruitment Source Contacts"}
        btnName={"Add Source Contact"}
        delete_action={"SOURCE_CONTACTS_DELETE"}
        add_action={"SOURCE_CONTACTS_ADD"}
        Data={rows}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "source contacts",
          "recruitment source contacts",
          "Add Source Contact",
          "source contact",
        ]}
        Route={"/recruitment/source-contacts"}
        setData={setRows}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_recruitment_source_contacts"
          primaryKey="organization_recruitment_source_contact_id"
          heading="Recruitment Source Contacts"
          data={rows}
          sortname="organization_recruitment_source_contact_id"
          showActions={true}
          Route="/recruitment/source-contacts"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={[
            "SOURCE_CONTACTS_DELETE",
            "SOURCE_CONTACTS_EDIT",
          ]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_recruitment_source_contacts"
          toolbarExtras={
            <ToolbarFilterSelect
              label="Recruitment Source"
              urlKey="recruitment_source"
              allLabel="All recruitment sources"
              options={recruitmentSources}
              valueKey="organization_recruitment_source_id"
              labelKey="recruitment_source_name"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
            />
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default RecruitmentSourceContactsList;
